import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { PopupEvent, TeamSectionItemType } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';

import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import { FilterSortType } from 'webapp/components/boards/select/base';
import BoardsSelectGridComponent from 'webapp/components/boards/select/grid';
import BoardsSelectTableComponent from 'webapp/components/boards/select/table';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import Board from 'webapp/models-custom/board';
import BoardFromAnotherSpace from 'webapp/models-custom/board-from-another-space';
import BoardLoop from 'webapp/models-custom/board-loop';
import {
	filterSettingsNamePrefix,
	viewSettingsNamePrefix,
} from 'webapp/models-custom/filter-sort/board-at-select-popup';
import {
	filterSettingsNamePrefix as filterSettingsNamePrefixForUpdate,
	viewSettingsNamePrefix as viewSettingsNamePrefixForUpdate,
} from 'webapp/models-custom/filter-sort/board-from-another-space-at-select-popup';
import Report from 'webapp/models-custom/report';
import TeamSection from 'webapp/models-custom/team-section';

import type { TeamSectionItemSnapshot } from 'webapp/services/model-manager/team-sections';

export type BoardSelectListItem = Board | BoardLoop | BoardFromAnotherSpace | Report;

export interface BoardSelectListSelection {
	boards: Board[];
	boardLoops: BoardLoop[];
	boardsFromAnotherSpace: BoardFromAnotherSpace[];
	reports: Report[];
}

export default class BoardsPopupSelectListComponent extends PopupInnerModalComponent<PopupData.Board.SelectList> {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;

	@tracked viewId = 'table';

	@tracked selected: BoardSelectListSelection = {
		boards: [],
		boardLoops: [],
		boardsFromAnotherSpace: [],
		reports: [],
	};

	@tracked model: BoardSelectListItem[] = [];
	@tracked isLoading = false;

	confirmed = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		void this.mmBoardLoopsService.requestBoardLoops();

		this.loadViewSettings();

		if (this.popupData.fetchBoardsFromTemplate) {
			this.isLoading = true;
			this.popupData.fetchBoardsFromTemplate().then(
				(boardsFromAnotherSpace) => {
					if (boardsFromAnotherSpace.length) {
						this.model = boardsFromAnotherSpace;
						this.isLoading = false;
					} else {
						this.infolayerService.ok('No modified dashboards found.');
						this.close();
					}
				},
				() => {
					this.close();
				},
			);
		} else {
			this.model = this.popupData.model;
		}

		this.args.model.on(PopupEvent.CLOSING, this, () => {
			if (!this.confirmed && this.popupData.onCancel) {
				this.popupData.onCancel();
			}
		});
	}

	get isEmpty(): boolean {
		if (!this.popupData.isReport && !this.isBoardsFromTemplate) {
			return this.model.length === 0;
		} else {
			return false;
		}
	}

	get isBoardsFromTemplate(): boolean {
		// if we get passed fetch function, we know it's for updating boards (from another space) from template
		return !!this.popupData.fetchBoardsFromTemplate;
	}

	get isTeamSection(): boolean {
		return !!this.popupData.isTeamSection;
	}

	get isForSelectingReports(): boolean {
		return !!this.popupData.isForSelectingReports;
	}

	get titleIcon(): IconName {
		return this.isForSelectingReports ? 'Reports' : 'Databoards';
	}

	get title(): string {
		if (this.popupData.title) {
			return this.popupData.title;
		}

		const prefix = 'Add ';
		if (this.popupData.isReport) {
			return `${prefix}dashboards to report`;
		} else if (this.isBoardsFromTemplate) {
			return 'Update dashboards from template';
		} else if (this.isTeamSection) {
			let model = 'dashboard or looped dashboard';
			if (this.isForSelectingReports) {
				model = 'report';
			}
			return `${prefix}${model} to the section`;
		}

		return `${prefix}dashboards to loop`;
	}

	// setting view
	get settingsName(): string {
		const viewSettingName = this.isBoardsFromTemplate
			? viewSettingsNamePrefixForUpdate
			: viewSettingsNamePrefix;
		return `${viewSettingName}-${this.accountService.id}`;
	}

	get isGridView(): boolean {
		return this.viewId === 'grid';
	}

	get filterSortType(): FilterSortType {
		if (this.isForSelectingReports) {
			return FilterSortType.REPORT;
		} else if (this.isBoardsFromTemplate) {
			return FilterSortType.BOARD_FROM_ANOTHER_SPACE;
		} else {
			return FilterSortType.BOARD;
		}
	}

	resetFilterSettings(): void {
		const filterSettingsName = this.isBoardsFromTemplate
			? filterSettingsNamePrefixForUpdate
			: filterSettingsNamePrefix;
		this.viewSettingsManagerService.save(
			`${filterSettingsName}-${this.accountService.id}`,
			{},
			true,
		);
	}

	loadViewSettings(): void {
		const savedValues =
			this.viewSettingsManagerService.load<{ viewId: string }>(this.settingsName, true) || {};
		if (savedValues.viewId) {
			this.viewId = savedValues.viewId;
		}
	}

	@action
	openView(viewId: string): void {
		this.viewId = viewId;
		this.viewSettingsManagerService.save(this.settingsName, { viewId }, true);
	}

	// selecting items
	get maxSelectable(): number {
		return this.popupData.maxSelectable || 0;
	}

	@cached
	get selectedBoardIds(): number[] {
		const { boards, boardLoops, boardsFromAnotherSpace } = this.selected;

		const selectedBoardIds = [
			...boards.map((item) => item.numericId),
			...boardsFromAnotherSpace.map((item) => item.id),
		] as number[];
		boardLoops.forEach((boardLoop) => {
			selectedBoardIds.push(...boardLoop.boardIds);
		});

		return uniq(selectedBoardIds);
	}

	@cached
	get selectedItemObjects(): TeamSectionItemSnapshot[] {
		const { boards, boardLoops, boardsFromAnotherSpace, reports } = this.selected;

		const selectedBoards = this.mapAndFilterObjects(
			boards,
			(board) => board.numericId,
			TeamSectionItemType.DATABOARD,
		);
		const selectedBoardLoops = this.mapAndFilterObjects(
			boardLoops,
			(boardLoop) => boardLoop.numericId,
			TeamSectionItemType.DATABOARD_LOOP,
		);
		const selectedBoardsFromAnotherSpace = this.mapAndFilterObjects(
			boardsFromAnotherSpace,
			(board) => board.id,
			TeamSectionItemType.DATABOARD,
		);
		const selectedReports = this.mapAndFilterObjects(
			reports,
			(report) => report.numericId,
			TeamSectionItemType.REPORT,
		);

		return [
			...selectedBoards,
			...selectedBoardLoops,
			...selectedBoardsFromAnotherSpace,
			...selectedReports,
		];
	}

	get uniqueBoardIdsCount(): number {
		return this.selectedBoardIds.length;
	}

	get selectedCount(): number {
		return (
			this.selected.boards.length +
			this.selected.boardLoops.length +
			this.selected.boardsFromAnotherSpace.length +
			this.selected.reports.length
		);
	}

	get isSelectBtnDisabled(): boolean {
		return !this.selectedCount;
	}

	get selectBtnText(): string {
		const selectedCount = this.selectedCount;
		let text = 'Select';

		if (selectedCount) {
			text += ` (${selectedCount})`;
		}

		return text;
	}

	// Caller override; the derived copy below only covers loops and team sections.
	get emptyState(): PopupData.Board.SelectList['emptyState'] {
		return this.popupData.emptyState;
	}

	get emptyListText(): string {
		if (this.emptyState) {
			return this.emptyState.text;
		}

		const actionWord = this.isTeamSection ? 'adding' : 'creating',
			prefix = `To start ${actionWord} `,
			middleText = ', you need to build ',
			suffix = ' first.';

		if (this.isTeamSection) {
			const modelTitle = this.isForSelectingReports
				? 'reports'
				: 'dashboards or looped dashboards';
			return `${prefix}section items${middleText}${modelTitle}${suffix}`;
		}
		return `${prefix}looped dashboards${middleText}dashboards${suffix}`;
	}

	get emptyListHeaderText(): string {
		if (this.emptyState) {
			return this.emptyState.header;
		}

		const prefix = 'No ',
			postfix = ' created yet';
		if (this.isTeamSection) {
			const modelTitle = this.isForSelectingReports
				? 'reports'
				: 'dashboards or looped dashboards';
			return `${prefix}${modelTitle}${postfix}`;
		}
		return `${prefix}dashboards${postfix}`;
	}

	get emptyListImageUrl(): string {
		const name = this.isForSelectingReports ? 'reports' : 'boards';
		// The dark twins redraw the near-black outlines light.
		const folder = this.navigationService.theme.isDark
			? '/images/placeholders/dark'
			: '/images/placeholders';

		return `${folder}/${name}.svg`;
	}

	showMaxError(): void {
		let errorText;
		if (this.popupData.maxSelectableError) {
			errorText = this.popupData.maxSelectableError;
		} else if (this.popupData.isReport || this.isBoardsFromTemplate) {
			errorText = 'Maximum number of dashboards reached!';
		} else if (this.isTeamSection) {
			errorText = `Maximum number of items in section is ${TeamSection.maxItems}!`;
		} else {
			errorText = `Maximum number of dashboards in a loop is ${BoardLoop.maxBoardsInLoop}!`;
		}
		this.infolayerService.error(errorText);
	}

	@action
	checkIfItemCanBeSelected(item: BoardSelectListItem): boolean {
		if (this.submitInProgress) {
			return false;
		}

		let itemsCounter = 0;
		// Report mode counts picked items; the board-id count below ignores selected reports.
		if (this.isTeamSection || this.isForSelectingReports) {
			const selectedObjects = this.selectedItemObjects;

			if (!this.isItemSelected(item) && selectedObjects.length >= this.maxSelectable) {
				this.showMaxError();
				return false;
			}
		} else {
			const selectedBoardIds = this.selectedBoardIds;

			if (item instanceof Board) {
				if (!selectedBoardIds.includes(item.numericId!)) {
					itemsCounter++;
				}
			} else if (item instanceof BoardLoop) {
				item.boardIds.forEach((boardId) => {
					if (!selectedBoardIds.includes(boardId)) {
						itemsCounter++;
					}
				});
			} else if (item instanceof BoardFromAnotherSpace) {
				if (!selectedBoardIds.includes(item.id)) {
					itemsCounter++;
				}
			} else if (item instanceof Report) {
				item.slides.forEach((slide) => {
					const boardId = slide.board?.numericId;
					if (boardId && !selectedBoardIds.includes(boardId)) {
						itemsCounter++;
					}
				});
			}

			if (selectedBoardIds.length + itemsCounter > this.maxSelectable) {
				this.showMaxError();
				return false;
			}
		}

		return true;
	}

	@action
	select(item: BoardSelectListItem): void {
		const selected = this.selected;

		if (item instanceof Board) {
			this.modifyArray(item, selected.boards);
		} else if (item instanceof BoardLoop) {
			this.modifyArray(item, selected.boardLoops);
		} else if (item instanceof BoardFromAnotherSpace) {
			this.modifyArray(item, selected.boardsFromAnotherSpace);
		} else {
			this.modifyArray(item, selected.reports);
		}

		this.selected = { ...selected };
	}

	@action
	checkIfAllItemsCanBeSelected(items: BoardSelectListItem[]): boolean {
		if (this.submitInProgress) {
			return false;
		}

		if (items.length > this.maxSelectable) {
			this.showMaxError();
			return false;
		}

		return true;
	}

	@action
	selectAll(items: BoardSelectListItem[]): void {
		const selected = this.selected;

		items.forEach((item) => {
			if (item instanceof Board) {
				if (!selected.boards.map((item) => item.id).includes(item.id)) {
					selected.boards.push(item);
				}
			} else if (item instanceof BoardLoop) {
				if (!selected.boardLoops.map((item) => item.id).includes(item.id)) {
					selected.boardLoops.push(item);
				}
			} else if (item instanceof BoardFromAnotherSpace) {
				if (!selected.boardsFromAnotherSpace.map((item) => item.id).includes(item.id)) {
					selected.boardsFromAnotherSpace.push(item);
				}
			} else if (item instanceof Report) {
				if (!selected.reports.map((item) => item.id).includes(item.id)) {
					selected.reports.push(item);
				}
			}
		});

		this.selected = { ...selected };
	}

	@action
	deselectAll(): void {
		this.selected = {
			boards: [],
			boardLoops: [],
			boardsFromAnotherSpace: [],
			reports: [],
		};
	}

	@action
	save(): void {
		this.confirmed = true;
		this.submitInProgress = true;
		void this.popupData
			.onConfirm(this.selectedBoardIds, this.selectedItemObjects)
			.then((doClose) => {
				if (doClose) {
					this.close();
				}
			})
			.finally(() => {
				this.submitInProgress = false;
			});
	}

	@action
	teardownView(): void {
		this.resetFilterSettings();
	}

	private isItemSelected(item: BoardSelectListItem): boolean {
		// Get all currently selected items
		const selectedItemObjects = this.selectedItemObjects;

		// Map item types to their corresponding TeamSectionItemType
		const typeMap = {
			[Board.name]: TeamSectionItemType.DATABOARD,
			[BoardLoop.name]: TeamSectionItemType.DATABOARD_LOOP,
			[Report.name]: TeamSectionItemType.REPORT,
		};

		// Get the TeamSectionItemType for the current item
		const objectType = typeMap[item.constructor.name];
		if (!objectType) return false;

		// Get the appropriate ID based on item type
		const itemId = item instanceof BoardFromAnotherSpace ? item.id : item.numericId;

		// Check if the item exists in the selected items
		return selectedItemObjects.some(
			(selectedItem) =>
				selectedItem.objectType === objectType && selectedItem.objectId === itemId,
		);
	}

	private modifyArray(item: BoardSelectListItem, array: BoardSelectListItem[]): void {
		const index = array.findIndex((element) => element.id === item.id);

		if (index >= 0) {
			array.splice(index, 1);
		} else {
			array.push(item);
		}
	}

	private mapAndFilterObjects<T>(
		items: T[],
		idGetter: (item: T) => number | null,
		objectType: TeamSectionItemType,
	): TeamSectionItemSnapshot[] {
		return items.map((item) => {
			const objectId = idGetter(item),
				snapshot: TeamSectionItemSnapshot = {
					objectType,
				};

			if (objectId !== null) {
				snapshot.objectId = objectId;
			}

			return snapshot;
		});
	}
	<template>
		<div class="title" {{willDestroy this.teardownView}}>
			<CommonUiIconComponent @name={{this.titleIcon}} @size="lg" />
			{{this.title}}
		</div>

		{{#if this.isLoading}}
			<div class="list-empty">
				<BarLoaderComponent @size="big" />
			</div>
		{{else if this.isEmpty}}
			<div class="list-empty">
				<img
					src={{this.emptyListImageUrl}}
					width="56"
					height="56"
					alt="Boards placeholder"
				/>
				<div class="header">{{this.emptyListHeaderText}}</div>
				<p>{{this.emptyListText}}</p>
				{{#unless this.navigationService.isMobileApp}}
					{{#if this.emptyState}}
						<LinkTo class="button size-big" @route={{this.emptyState.ctaRoute}}>
							{{this.emptyState.ctaLabel}}
						</LinkTo>
					{{else if this.isTeamSection}}
						{{#if this.isForSelectingReports}}
							<LinkTo class="button size-big" @route="reports">
								Build reports
							</LinkTo>
						{{else}}
							<LinkTo class="button size-big" @route="boards">
								Build dashboards
							</LinkTo>
							<LinkTo class="button size-big" @route="boards.loops">
								Build looped dashboards
							</LinkTo>
						{{/if}}
					{{else}}
						<LinkTo class="button size-big" @route="boards">
							Build dashboards
						</LinkTo>
					{{/if}}
				{{/unless}}
			</div>
		{{else}}
			{{#if this.isGridView}}
				<BoardsSelectGridComponent
					@model={{this.model}}
					@selected={{this.selected}}
					@checkIfAllItemsCanBeSelected={{this.checkIfAllItemsCanBeSelected}}
					@checkIfItemCanBeSelected={{this.checkIfItemCanBeSelected}}
					@onSelect={{this.select}}
					@onSelectAll={{this.selectAll}}
					@onDeselectAll={{this.deselectAll}}
					@onOpenView={{this.openView}}
					@onReposition={{this.reposition}}
					@filterSortType={{this.filterSortType}}
				/>
			{{else}}
				<BoardsSelectTableComponent
					@model={{this.model}}
					@selected={{this.selected}}
					@checkIfAllItemsCanBeSelected={{this.checkIfAllItemsCanBeSelected}}
					@checkIfItemCanBeSelected={{this.checkIfItemCanBeSelected}}
					@onSelect={{this.select}}
					@onSelectAll={{this.selectAll}}
					@onDeselectAll={{this.deselectAll}}
					@onOpenView={{this.openView}}
					@onReposition={{this.reposition}}
					@filterSortType={{this.filterSortType}}
				/>
			{{/if}}

			<div class="controls2 controls2--sticky">
				<SubmitButtonComponent
					@text="Cancel"
					@theme="grey-empty"
					@noDelay={{true}}
					@onTrigger={{this.close}}
				/>
				<SubmitButtonComponent
					@text={{this.selectBtnText}}
					@extraClass="save"
					@isDisabled={{this.isSelectBtnDisabled}}
					@onTrigger={{this.save}}
					@submitInProgress={{this.submitInProgress}}
				/>
			</div>
		{{/if}}
	</template>
}
